import { ClearinghouseTransactionType, PayerContact } from '../../api';
import { PayerDataKind } from './payer-routing';

/**
 * The payer fields routing actually reads — nothing more.
 *
 * Typed structurally rather than as `SupportPayer` because the provider app renders the same
 * routing from `ProviderPayer`, and the two models are NOT assignable to one another:
 * `SupportPayer extends Tags` with `SupportTag[]`, while `ProviderPayer` carries
 * `ProviderTag[]`. Naming the three fields that matter lets one derivation serve both apps,
 * which is the whole reason this module lives in `core` (NS-1).
 *
 * Widening a parameter cannot break an existing caller: every `SupportPayer` already
 * satisfies this.
 */
export interface IRoutablePayer {
    name?: string;
    contact?: PayerContact;
    selfPay?: boolean;
}

/** The clearinghouse-connection fields routing reads. `Support`/`ProviderClearinghousePayer` both fit. */
export interface IRoutableConnection {
    externalId?: string;
    transactions?: ClearinghouseTransactionType[];
    clearinghouse?: { name?: string };
    payer?: { name?: string };
}

/**
 * The nine lanes in the design's "where each kind of data goes" table.
 *
 * This is a different model from `PayerDataKind`, and the difference is easy to miss. The
 * five `PayerDataKind`s are `PayerContact`'s purposes and back the "drop-to-paper paths"
 * panel ("2 of 5 set"). These nine are every kind of data that moves between us and a payer,
 * whether it moves electronically or on paper ("7 of 9 routed"). Six of them ride an X12
 * transaction; three are paper by definition because X12 has nothing that carries them.
 *
 * 834 (benefit enrolment) has no lane on purpose — it appears in the EDI grid because a
 * clearinghouse may support it, but it is not correspondence about a claim.
 */

export type PayerLaneId =
    | 'claim'
    | 'eligibility'
    | 'status'
    | 'authorization'
    | 'remittance'
    | 'paperwork'
    | 'reconsideration'
    | 'overpayment'
    | 'other';

/** Which way the data moves. Remittance is the only inbound one. */
export type PayerLaneDirection = 'Outbound' | 'Two-way' | 'Inbound';

export type PayerLanePath = 'EDI' | 'Letter' | 'Fax' | 'Email' | 'Statement' | 'Not routed';

export interface IPayerLane {
    id: PayerLaneId;
    label: string;
    /** The small line under the label — "837 professional / institutional", "always paper". */
    sub: string;
    direction: PayerLaneDirection;
    path: PayerLanePath;
    /** Where it lands. `detail` is the second line under it, blank when there is nothing to add. */
    destination: string;
    detail: string;
}

interface ILaneSpec {
    id: PayerLaneId;
    label: string;
    sub: string;
    direction: PayerLaneDirection;
    /** X12 transactions that can carry this lane. Empty means paper by definition. */
    transactions: ClearinghouseTransactionType[];
    /**
     * The `PayerContact` purpose to fall back to on paper. Undefined means there is no paper
     * path for this kind of data at all — an unsupported transaction is simply unroutable,
     * which is what the design shows for Authorizations.
     */
    paperKind?: PayerDataKind;
}

const LANES: ILaneSpec[] = [
    {
        id: 'claim',
        label: 'Claim data',
        sub: '837 professional / institutional',
        direction: 'Outbound',
        transactions: [
            ClearinghouseTransactionType.EDI837P,
            ClearinghouseTransactionType.EDI837I,
            ClearinghouseTransactionType.EDI837D,
        ],
        paperKind: 'claim',
    },
    {
        id: 'eligibility',
        label: 'Eligibility checks',
        sub: '270 / 271',
        direction: 'Two-way',
        transactions: [ClearinghouseTransactionType.EDI270271],
    },
    {
        id: 'status',
        label: 'Claim statuses',
        sub: '276 / 277',
        direction: 'Two-way',
        transactions: [ClearinghouseTransactionType.EDI276277],
    },
    {
        id: 'authorization',
        label: 'Authorizations',
        sub: '278',
        direction: 'Two-way',
        transactions: [ClearinghouseTransactionType.EDI278],
    },
    {
        id: 'remittance',
        label: 'Remittance',
        sub: '835 inbound',
        direction: 'Inbound',
        transactions: [ClearinghouseTransactionType.EDI835],
    },
    {
        id: 'paperwork',
        label: 'Supplemental paperwork',
        sub: '275 attachment or records',
        direction: 'Outbound',
        transactions: [ClearinghouseTransactionType.EDI275],
        paperKind: 'paperwork',
    },
    {
        id: 'reconsideration',
        label: 'Reconsideration data',
        sub: 'always paper',
        direction: 'Outbound',
        transactions: [],
        paperKind: 'reconsideration',
    },
    {
        id: 'overpayment',
        label: 'Overpayments and refunds',
        sub: 'paper',
        direction: 'Two-way',
        transactions: [],
        paperKind: 'overpayment',
    },
    {
        id: 'other',
        label: 'Other correspondence',
        sub: 'paper',
        direction: 'Two-way',
        transactions: [],
        paperKind: 'other',
    },
];

const CONTACT_SUFFIX: Record<PayerDataKind, 'Claim' | 'Appeal' | 'Documentation' | 'Overpayment' | 'Other'> = {
    claim: 'Claim',
    reconsideration: 'Appeal',
    paperwork: 'Documentation',
    overpayment: 'Overpayment',
    other: 'Other',
};

/**
 * The postal lines, not the name.
 *
 * `Address.name` is the department — "Reconsiderations Department" — and belongs on the
 * detail line beside it, which is how the design reads. Folding it in here instead of
 * `lines` meant the PO box never rendered at all.
 */
function formatAddress(address: IAddressish): string {
    return [...(address.lines || []), address.city, address.state, address.postal].filter(Boolean).join(', ');
}

/**
 * Fax, then email, then post.
 *
 * All three are "paper" as far as routing goes, but they are not equally actionable: a fax
 * number or an inbox reaches the payer today, an address is measured in days. Showing the
 * fastest configured channel is what an operator deciding how to chase a claim needs.
 */
interface IAddressish {
    name?: string;
    lines?: string[];
    city?: string;
    state?: string;
    postal?: string;
}

export function getPaperPath(
    contact: PayerContact | undefined,
    kind: PayerDataKind,
): { path: PayerLanePath; destination: string; name?: string } | undefined {
    const suffix = CONTACT_SUFFIX[kind];
    const fax = contact?.[`fax${suffix}` as keyof PayerContact] as string | undefined;
    if (fax) {
        return { path: 'Fax', destination: fax };
    }

    const email = contact?.[`email${suffix}` as keyof PayerContact] as string | undefined;
    if (email) {
        return { path: 'Email', destination: email };
    }

    const address = contact?.[`address${suffix}` as keyof PayerContact] as IAddressish | undefined;
    if (address) {
        return { path: 'Letter', destination: formatAddress(address), name: address.name };
    }

    return undefined;
}

function electronicCarrier(
    spec: ILaneSpec,
    bindings: IRoutableConnection[],
): { binding: IRoutableConnection; transaction: ClearinghouseTransactionType } | undefined {
    for (const binding of bindings) {
        const match = (binding.transactions || []).find(t => spec.transactions.includes(t));
        if (match) {
            return { binding, transaction: match };
        }
    }

    return undefined;
}

/**
 * @param bindings the payer's clearinghouse connections, or `undefined` while the join is in
 *                 flight. Undefined yields `Not routed` only for lanes that cannot be
 *                 answered without it — paper-by-definition lanes resolve regardless.
 */
export function getPayerLanes(payer: IRoutablePayer, bindings: IRoutableConnection[] | undefined): IPayerLane[] {
    return LANES.map(spec => {
        const base = { id: spec.id, label: spec.label, sub: spec.sub, direction: spec.direction };

        // A self-pay payer is billed through patient collections rather than a payer channel.
        if (payer.selfPay) {
            return { ...base, path: 'Statement' as PayerLanePath, destination: 'Patient collections', detail: '' };
        }

        const carrier = spec.transactions.length > 0 && bindings ? electronicCarrier(spec, bindings) : undefined;

        if (carrier) {
            const clearinghouse = carrier.binding.clearinghouse?.name || 'Clearinghouse';
            const payerName = carrier.binding.payer?.name || payer.name;

            return {
                ...base,
                path: 'EDI' as PayerLanePath,
                destination: `${clearinghouse} · ${payerName}`,
                detail: `${carrier.transaction} · EDI ${carrier.binding.externalId || '—'}`,
            };
        }

        const paper = spec.paperKind ? getPaperPath(payer.contact, spec.paperKind) : undefined;

        if (paper) {
            return {
                ...base,
                path: paper.path,
                destination: paper.destination,
                detail: [paper.name, spec.transactions.length === 0 ? 'paper by definition' : 'drops to paper']
                    .filter(Boolean)
                    .join(' · '),
            };
        }

        // No destination string: the chip already says "Not routed", so an em-dash beside it
        // only repeats it and pushes the one useful line — why, and what to do — down a row.
        return {
            ...base,
            path: 'Not routed' as PayerLanePath,
            destination: '',
            detail: spec.paperKind
                ? 'Add a paper path for this category'
                : 'Not supported by the clearinghouse payer and no paper path set',
        };
    });
}

/** "7 of 9 routed" in the design's section header. */
export function countRouted(lanes: IPayerLane[]): { routed: number; total: number } {
    return { routed: lanes.filter(l => l.path !== 'Not routed').length, total: lanes.length };
}
